/**
 * ERPNext Shopping Cart API Service
 * Pure API layer - no state management, just HTTP calls
 */

const ERPNEXT_URL = import.meta.env.VITE_ERPNEXT_URL || '';

export interface CartItem {
  name: string; // Quotation Item ID
  item_code: string;
  item_name: string;
  qty: number;
  rate: number;
  amount: number;
  image?: string;
  warehouse?: string;
  additional_notes?: string;
}

export interface CartQuotation {
  name: string; // Quotation ID
  quotation_to: string;
  party_name: string;
  items: CartItem[];
  total_qty: number;
  total: number;
  grand_total: number;
  currency: string;
  customer_address?: string;
  shipping_address_name?: string;
}

export interface CartResponse {
  doc: CartQuotation;
  shipping_addresses: Address[];
  billing_addresses: Address[];
  shipping_rules: any[];
  cart_settings: any;
}

export interface Address {
  name: string;
  title: string;
  display: string;
}

/**
 * Get current shopping cart (Quotation)
 */
export async function getCart(): Promise<CartResponse> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/method/webshop.webshop.shopping_cart.cart.get_cart_quotation`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }

    const data = await response.json();
    return data.message;
  } catch (error) {
    console.error('Get cart error:', error);
    throw error;
  }
}

/**
 * Add or update item in cart
 * @param itemCode - Item code to add/update
 * @param qty - Quantity (0 to remove)
 * @param additionalNotes - Optional notes for the item
 */
export async function updateCartItem(
  itemCode: string,
  qty: number,
  additionalNotes?: string
): Promise<void> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/method/webshop.webshop.shopping_cart.cart.update_cart`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          item_code: itemCode,
          qty: qty,
          additional_notes: additionalNotes,
          with_items: 1,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update cart');
    }
  } catch (error) {
    console.error('Update cart error:', error);
    throw error;
  }
}

/**
 * Remove item from cart
 */
export async function removeCartItem(itemCode: string): Promise<void> {
  return updateCartItem(itemCode, 0);
}

/**
 * Clear entire cart
 */
export async function clearCart(): Promise<void> {
  try {
    const cart = await getCart();

    // Remove all items by setting qty to 0
    const removePromises = cart.doc.items.map((item) =>
      updateCartItem(item.item_code, 0)
    );

    await Promise.all(removePromises);
  } catch (error) {
    console.error('Clear cart error:', error);
    throw error;
  }
}

/**
 * Place order - converts cart (Quotation) to Sales Order
 */
export async function placeOrder(): Promise<string> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/method/webshop.webshop.shopping_cart.cart.place_order`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to place order');
    }

    const data = await response.json();
    return data.message; // Sales Order ID
  } catch (error) {
    console.error('Place order error:', error);
    throw error;
  }
}

/**
 * Get shipping addresses for current user
 */
export async function getShippingAddresses(): Promise<Address[]> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/method/webshop.webshop.shopping_cart.cart.get_shipping_addresses`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch shipping addresses');
    }

    const data = await response.json();
    return data.message;
  } catch (error) {
    console.error('Get shipping addresses error:', error);
    throw error;
  }
}

/**
 * Get billing addresses for current user
 */
export async function getBillingAddresses(): Promise<Address[]> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/method/webshop.webshop.shopping_cart.cart.get_billing_addresses`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch billing addresses');
    }

    const data = await response.json();
    return data.message;
  } catch (error) {
    console.error('Get billing addresses error:', error);
    throw error;
  }
}

/**
 * Update cart address
 * @param addressType - 'billing' or 'shipping'
 * @param addressName - Address document name
 */
export async function updateCartAddress(
  addressType: 'billing' | 'shipping',
  addressName: string
): Promise<void> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/method/webshop.webshop.shopping_cart.cart.update_cart_address`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          address_type: addressType,
          address_name: addressName,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update address');
    }
  } catch (error) {
    console.error('Update address error:', error);
    throw error;
  }
}

/**
 * Apply coupon code to cart
 */
export async function applyCouponCode(couponCode: string): Promise<void> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/method/webshop.webshop.shopping_cart.cart.apply_coupon_code`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          applied_code: couponCode,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to apply coupon');
    }
  } catch (error) {
    console.error('Apply coupon error:', error);
    throw error;
  }
}

/**
 * Remove coupon code from cart
 */
export async function removeCouponCode(): Promise<void> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/method/webshop.webshop.shopping_cart.cart.remove_coupon_code`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to remove coupon');
    }
  } catch (error) {
    console.error('Remove coupon error:', error);
    throw error;
  }
}
